import { supabase } from '../lib/supabase';

/**
 * Service for managing prompt fields (simplified prompt instruction management)
 * Handles snake_case (database) to camelCase (React) conversion
 */
export const promptFieldService = {
  /**
   * Get all prompt fields
   * @returns {Promise<Array>} Array of prompt fields in camelCase
   */
  async getAllPromptFields() {
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.select('*')
      ?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      useCaseId: row?.use_case_id,
      aiToolId: row?.ai_tool_id,
      fieldName: row?.field_name,
      fieldLabel: row?.field_label,
      fieldType: row?.field_type,
      placeholder: row?.placeholder,
      description: row?.description,
      isRequired: row?.is_required,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Get prompt fields for a specific use case and AI tool
   * @param {string} useCaseId - UUID of the use case
   * @param {string} aiToolId - UUID of the AI tool
   * @returns {Promise<Array>} Array of prompt fields
   */
  async getPromptFieldsByUseCaseAndTool(useCaseId, aiToolId) {
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.select('*')
      ?.eq('use_case_id', useCaseId)
      ?.eq('ai_tool_id', aiToolId)
      ?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      useCaseId: row?.use_case_id,
      aiToolId: row?.ai_tool_id,
      fieldName: row?.field_name,
      fieldLabel: row?.field_label,
      fieldType: row?.field_type,
      placeholder: row?.placeholder,
      description: row?.description,
      isRequired: row?.is_required,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Get prompt fields for a specific use case (all AI tools)
   * @param {string} useCaseId - UUID of the use case
   * @returns {Promise<Array>} Array of prompt fields
   */
  async getPromptFieldsByUseCase(useCaseId) {
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.select(`
        *,
        ai_tools (
          id,
          name,
          value
        )
      `)
      ?.eq('use_case_id', useCaseId)
      ?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      useCaseId: row?.use_case_id,
      aiToolId: row?.ai_tool_id,
      fieldName: row?.field_name,
      fieldLabel: row?.field_label,
      fieldType: row?.field_type,
      placeholder: row?.placeholder,
      description: row?.description,
      isRequired: row?.is_required,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      aiTool: row?.ai_tools ? {
        id: row?.ai_tools?.id,
        name: row?.ai_tools?.name,
        value: row?.ai_tools?.value
      } : null
    })) || [];
  },

  /**
   * Get prompt fields for a specific AI tool (all use cases)
   * @param {string} aiToolId - UUID of the AI tool
   * @returns {Promise<Array>} Array of prompt fields
   */
  async getPromptFieldsByAITool(aiToolId) {
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.select(`
        *,
        use_cases (
          id,
          name
        )
      `)
      ?.eq('ai_tool_id', aiToolId)
      ?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      useCaseId: row?.use_case_id,
      aiToolId: row?.ai_tool_id,
      fieldName: row?.field_name,
      fieldLabel: row?.field_label,
      fieldType: row?.field_type,
      placeholder: row?.placeholder,
      description: row?.description,
      isRequired: row?.is_required,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at,
      useCase: row?.use_cases ? {
        id: row?.use_cases?.id,
        name: row?.use_cases?.name
      } : null
    })) || [];
  },

  /**
   * Create a new prompt field
   * @param {Object} field - Prompt field data in camelCase
   * @returns {Promise<Object>} Created prompt field
   */
  async createPromptField(field) {
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.insert({
        use_case_id: field?.useCaseId,
        ai_tool_id: field?.aiToolId,
        field_name: field?.fieldName,
        field_label: field?.fieldLabel,
        field_type: field?.fieldType || 'text',
        placeholder: field?.placeholder,
        description: field?.description,
        is_required: field?.isRequired || false,
        display_order: field?.displayOrder || 0
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      fieldName: data?.field_name,
      fieldLabel: data?.field_label,
      fieldType: data?.field_type,
      placeholder: data?.placeholder,
      description: data?.description,
      isRequired: data?.is_required,
      displayOrder: data?.display_order,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update an existing prompt field
   * @param {string} id - UUID of the prompt field
   * @param {Object} updates - Updated fields in camelCase
   * @returns {Promise<Object>} Updated prompt field
   */
  async updatePromptField(id, updates) {
    const dbUpdates = {};
    if (updates?.useCaseId !== undefined) dbUpdates.use_case_id = updates?.useCaseId;
    if (updates?.aiToolId !== undefined) dbUpdates.ai_tool_id = updates?.aiToolId;
    if (updates?.fieldName !== undefined) dbUpdates.field_name = updates?.fieldName;
    if (updates?.fieldLabel !== undefined) dbUpdates.field_label = updates?.fieldLabel;
    if (updates?.fieldType !== undefined) dbUpdates.field_type = updates?.fieldType;
    if (updates?.placeholder !== undefined) dbUpdates.placeholder = updates?.placeholder;
    if (updates?.description !== undefined) dbUpdates.description = updates?.description;
    if (updates?.isRequired !== undefined) dbUpdates.is_required = updates?.isRequired;
    if (updates?.displayOrder !== undefined) dbUpdates.display_order = updates?.displayOrder;
    dbUpdates.updated_at = new Date()?.toISOString();
    
    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.update(dbUpdates)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      fieldName: data?.field_name,
      fieldLabel: data?.field_label,
      fieldType: data?.field_type,
      placeholder: data?.placeholder,
      description: data?.description,
      isRequired: data?.is_required,
      displayOrder: data?.display_order,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete a prompt field
   * @param {string} id - UUID of the prompt field
   * @returns {Promise<void>}
   */
  async deletePromptField(id) {
    const { error } = await supabase
      ?.from('prompt_fields')
      ?.delete()
      ?.eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Bulk create prompt fields for an AI tool within a use case
   * @param {string} useCaseId - UUID of the use case
   * @param {string} aiToolId - UUID of the AI tool
   * @param {Array} fields - Array of field definitions
   * @returns {Promise<Array>} Created prompt fields
   */
  async bulkCreatePromptFields(useCaseId, aiToolId, fields) {
    const insertData = fields?.map((field, index) => ({
      use_case_id: useCaseId,
      ai_tool_id: aiToolId,
      field_name: field?.fieldName,
      field_label: field?.fieldLabel,
      field_type: field?.fieldType || 'text',
      placeholder: field?.placeholder,
      description: field?.description,
      is_required: field?.isRequired || false,
      display_order: field?.displayOrder !== undefined ? field?.displayOrder : index
    }));

    const { data, error } = await supabase
      ?.from('prompt_fields')
      ?.insert(insertData)
      ?.select();
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      useCaseId: row?.use_case_id,
      aiToolId: row?.ai_tool_id,
      fieldName: row?.field_name,
      fieldLabel: row?.field_label,
      fieldType: row?.field_type,
      placeholder: row?.placeholder,
      description: row?.description,
      isRequired: row?.is_required,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Delete all prompt fields for a specific use case and AI tool
   * @param {string} useCaseId - UUID of the use case
   * @param {string} aiToolId - UUID of the AI tool
   * @returns {Promise<void>}
   */
  async deletePromptFieldsByUseCaseAndTool(useCaseId, aiToolId) {
    const { error } = await supabase
      ?.from('prompt_fields')
      ?.delete()
      ?.eq('use_case_id', useCaseId)
      ?.eq('ai_tool_id', aiToolId);
    
    if (error) throw error;
  },

  /**
   * Get all options for a specific prompt field
   * @param {string} promptFieldId - UUID of the prompt field
   * @returns {Promise<Array>} Array of field options
   */
  async getPromptFieldOptions(promptFieldId) {
    const { data, error } = await supabase
      ?.from('prompt_field_options')
      ?.select('*')
      ?.eq('prompt_field_id', promptFieldId)
      ?.order('display_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      promptFieldId: row?.prompt_field_id,
      optionValue: row?.option_value,
      optionLabel: row?.option_label,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Create a new field option
   * @param {Object} option - Field option data in camelCase
   * @returns {Promise<Object>} Created field option
   */
  async createPromptFieldOption(option) {
    const { data, error } = await supabase
      ?.from('prompt_field_options')
      ?.insert({
        prompt_field_id: option?.promptFieldId,
        option_value: option?.optionValue,
        option_label: option?.optionLabel,
        display_order: option?.displayOrder || 0
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      promptFieldId: data?.prompt_field_id,
      optionValue: data?.option_value,
      optionLabel: data?.option_label,
      displayOrder: data?.display_order,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update an existing field option
   * @param {string} id - UUID of the field option
   * @param {Object} updates - Updated fields in camelCase
   * @returns {Promise<Object>} Updated field option
   */
  async updatePromptFieldOption(id, updates) {
    const dbUpdates = {};
    if (updates?.optionValue !== undefined) dbUpdates.option_value = updates?.optionValue;
    if (updates?.optionLabel !== undefined) dbUpdates.option_label = updates?.optionLabel;
    if (updates?.displayOrder !== undefined) dbUpdates.display_order = updates?.displayOrder;
    dbUpdates.updated_at = new Date()?.toISOString();
    
    const { data, error } = await supabase
      ?.from('prompt_field_options')
      ?.update(dbUpdates)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      promptFieldId: data?.prompt_field_id,
      optionValue: data?.option_value,
      optionLabel: data?.option_label,
      displayOrder: data?.display_order,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete a field option
   * @param {string} id - UUID of the field option
   * @returns {Promise<void>}
   */
  async deletePromptFieldOption(id) {
    const { error } = await supabase
      ?.from('prompt_field_options')
      ?.delete()
      ?.eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Bulk create field options for a prompt field
   * @param {string} promptFieldId - UUID of the prompt field
   * @param {Array} options - Array of options {optionValue, optionLabel, displayOrder}
   * @returns {Promise<Array>} Created field options
   */
  async bulkCreatePromptFieldOptions(promptFieldId, options) {
    const insertData = options?.map((opt, index) => ({
      prompt_field_id: promptFieldId,
      option_value: opt?.optionValue,
      option_label: opt?.optionLabel,
      display_order: opt?.displayOrder !== undefined ? opt?.displayOrder : index
    }));

    const { data, error } = await supabase
      ?.from('prompt_field_options')
      ?.insert(insertData)
      ?.select();
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      promptFieldId: row?.prompt_field_id,
      optionValue: row?.option_value,
      optionLabel: row?.option_label,
      displayOrder: row?.display_order,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  /**
   * Import prompt fields from JSON data - overrides existing data for the use case and AI tool
   * @param {Object} importData - JSON data with useCase, aiTools array containing toolName and promptFields
   * @returns {Promise<Object>} Import result with counts
   */
  async importPromptFieldsForUseCaseAndTool(importData) {
    try {
      const useCaseName = importData?.useCase;
      const aiToolData = importData?.aiTools?.[0]; // Get first AI tool from array
      const aiToolName = aiToolData?.toolName;
      const promptFields = aiToolData?.promptFields || [];

      if (!useCaseName || !aiToolName) {
        throw new Error('Use case name and AI tool name are required');
      }

      // Step 1: Find use case by name
      const { data: useCaseData, error: useCaseError } = await supabase
        ?.from('use_cases')
        ?.select('id')
        ?.eq('name', useCaseName)
        ?.single();

      if (useCaseError || !useCaseData) {
        throw new Error(`Use case "${useCaseName}" not found`);
      }

      const useCaseId = useCaseData?.id;

      // Step 2: Find AI tool by name
      const { data: aiToolDataResult, error: aiToolError } = await supabase
        ?.from('ai_tools')
        ?.select('id')
        ?.eq('name', aiToolName)
        ?.single();

      if (aiToolError || !aiToolDataResult) {
        throw new Error(`AI tool "${aiToolName}" not found`);
      }

      const aiToolId = aiToolDataResult?.id;

      // Step 3: Delete existing prompt fields for this use case and AI tool
      // This will cascade delete the prompt_field_options as well
      await this.deletePromptFieldsByUseCaseAndTool(useCaseId, aiToolId);

      // Step 4: Create new prompt fields
      const createdFields = [];
      let totalOptionsCreated = 0;

      for (const field of promptFields) {
        // Create the prompt field
        const createdField = await this.createPromptField({
          useCaseId,
          aiToolId,
          fieldName: field?.fieldName,
          fieldLabel: field?.fieldLabel,
          fieldType: field?.fieldType || 'TextArea',
          placeholder: field?.placeholder,
          description: field?.description,
          isRequired: field?.required || false,
          displayOrder: field?.order || 0
        });

        createdFields?.push(createdField);

        // Step 5: Create dropdown options if they exist
        if (field?.dropdownOptions && field?.dropdownOptions?.length > 0) {
          const options = field?.dropdownOptions?.map(opt => ({
            optionValue: opt?.value,
            optionLabel: opt?.label,
            displayOrder: opt?.order || 0
          }));

          const createdOptions = await this.bulkCreatePromptFieldOptions(
            createdField?.id,
            options
          );

          totalOptionsCreated += createdOptions?.length;
        }
      }

      return {
        success: true,
        useCaseId,
        aiToolId,
        fieldsCreated: createdFields?.length,
        optionsCreated: totalOptionsCreated,
        message: `Successfully imported ${createdFields?.length} fields with ${totalOptionsCreated} dropdown options for ${useCaseName} - ${aiToolName}`
      };
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  }
};

export default promptFieldService;